import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { EntityInstance } from '../types';
import {
  useEntityQueryOrchestration,
  useEntitySuppression,
  SuppressionAction,
  useEntityAnalysisModalNavigation,
  type EntityAnalysisModalNavigationState,
} from '../hooks';
import { EntityAnalysisModalTab } from '../../../shared/constants';
import { eventBus } from '../../../shared/providers/shared/utils';
import { buildExcludedEntitiesQuery } from '../utils';
import { EntityEdrProvider } from './EntityEdrContext';
import { EDRIntegration } from '@investigator/features/integrations/pages';

export interface EntitiesContextType {
  selectedEntity?: EntityInstance;
  setSelectedEntity: Dispatch<SetStateAction<EntityInstance | undefined>>;

  // Modal state
  analysisModal: {
    isOpen: boolean;
    onOpen: (entity?: EntityInstance) => void;
    onClose: () => void;
    activeTab: EntityAnalysisModalTab;
    setActiveTab: Dispatch<SetStateAction<EntityAnalysisModalTab>>;
    modalNavigation: EntityAnalysisModalNavigationState;
  };
  suppressionModal: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  isolationModal: {
    isOpen: boolean;
    onOpen: (type: EDRIntegration) => void;
    onClose: () => void;
    isolationType?: EDRIntegration;
  };

  // Entity suppression (mutations + validation)
  suppression: {
    pendingUpdate: boolean;
    error: string | null;
    enabled: boolean; // Can the entity be suppressed/unsuppressed at all?
    loading: boolean;
    updateSuppression: (
      action: SuppressionAction,
    ) => Promise<{ success: boolean; error?: string }>;
  };
}

// Re-export enums for convenience
export { SuppressionAction };

const EntitiesContext = createContext<EntitiesContextType | undefined>(
  undefined,
);

export const EntitiesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  // Core entity state
  const [selectedEntity, setSelectedEntity] = useState<
    EntityInstance | undefined
  >();

  // Get global date range from URL query params for fallback
  const globalDateRange = useDateRangeQueryString();

  // Modal states - single state for both analysis and information modals
  const {
    isOpen: isAnalysisModalOpen,
    onClose: chakraOnAnalysisModalClose,
    onOpen: onAnalysisModalOpenInternal,
  } = useDisclosure();

  const {
    isOpen: isolationOpen,
    onClose: onIsolationClose,
    onOpen: onIsolationOpenInternal,
  } = useDisclosure();

  const {
    isOpen: suppressionOpen,
    onClose: onSuppressionClose,
    onOpen: onSuppressionOpen,
  } = useDisclosure();

  // EntityAnalysisModal specific state
  const [activeTab, setActiveTab] = useState<EntityAnalysisModalTab>(
    EntityAnalysisModalTab.EntityOverview,
  );

  // EntityAnalysisModal navigation
  const modalNavigation = useEntityAnalysisModalNavigation();

  // Isolation state
  const [isolationType, setIsolationType] = useState<
    EDRIntegration | undefined
  >();

  // Entity suppression hook - mutations + validation
  const suppression = useEntitySuppression({
    selectedEntity,
    setSelectedEntity,
  });

  // Query orchestration for debounced queries
  useEntityQueryOrchestration({
    selectedEntity,
    setSelectedEntity,
    isEntityModalOpen: isAnalysisModalOpen,
  });

  // Enhanced close handler for EntityAnalysisModal with state cleanup
  const onAnalysisModalClose = useCallback(() => {
    chakraOnAnalysisModalClose();
    // Reset to clean state after modal animation completes (300ms)
    setTimeout(() => {
      setSelectedEntity(undefined);
      setActiveTab(EntityAnalysisModalTab.EntityOverview);
      modalNavigation.goBackToMain();
    }, 300);
  }, [chakraOnAnalysisModalClose, modalNavigation]);

  // Reset tab when entity changes in analysis modal
  useEffect(() => {
    if (selectedEntity?.entityId && isAnalysisModalOpen) {
      setActiveTab(EntityAnalysisModalTab.EntityOverview);
    }
  }, [selectedEntity?.entityId, isAnalysisModalOpen]);

  // Handle user logout - clear all sensitive data
  useEffect(() => {
    const handleLogout = () => {
      setSelectedEntity(undefined);
      setActiveTab(EntityAnalysisModalTab.EntityOverview);
      if (isAnalysisModalOpen) {
        chakraOnAnalysisModalClose();
      }
      if (isolationOpen) {
        onIsolationClose();
      }
      if (suppressionOpen) {
        onSuppressionClose();
      }
    };
    eventBus.on('user-logout', handleLogout);
    return () => eventBus.off('user-logout', handleLogout);
  }, [
    isAnalysisModalOpen,
    isolationOpen,
    suppressionOpen,
    chakraOnAnalysisModalClose,
    onIsolationClose,
    onSuppressionClose,
  ]);

  // Update selectedEntity when it changes to ensure it has excludedEntitiesVariables and normalized date ranges
  useEffect(() => {
    const needsVariables =
      selectedEntity && !selectedEntity.excludedEntitiesVariables;
    const needsTimeout = selectedEntity && !selectedEntity.timeoutId;
    const needsLastSeenInitialization =
      selectedEntity &&
      (selectedEntity.lastSeen === undefined || selectedEntity.lastSeen === 0);
    const needsDateNormalization =
      selectedEntity?.entityId &&
      (selectedEntity?.start === undefined ||
        selectedEntity?.end === undefined);

    if (
      needsVariables ||
      needsTimeout ||
      needsDateNormalization ||
      needsLastSeenInitialization
    ) {
      setSelectedEntity((prev) => {
        if (!prev) return prev;

        const updates: Partial<EntityInstance> = {};

        // Build excludedEntitiesVariables if missing
        if (needsVariables) {
          const variables = buildExcludedEntitiesQuery({
            entityName: prev.entityName || '',
            entityType: prev.entityType || '',
            tenantId: prev.tenantInfo?.tenant_id || '',
            contentId: prev.detectionData?.content_id ?? undefined,
          });
          if (variables) {
            updates.excludedEntitiesVariables = variables;
          }
          // Clear entityIsSuppressed when entity changes so we don't show stale data
          // The query will repopulate this value
          updates.entityIsSuppressed = undefined;
        }

        // Set timeoutId if missing (for immediate queries when modal opens)
        if (needsTimeout) {
          updates.timeoutId = uuidv4();
        }
        if (needsLastSeenInitialization) {
          updates.lastSeen = prev.lastSeen || globalDateRange.end;
        }

        // Normalize date range: use entity-specific dates if provided, otherwise fall back to global dates
        // This ensures all child components and contexts can use selectedEntity.start/end directly
        // without repeating the fallback logic everywhere
        if (needsDateNormalization) {
          updates.start = prev.start ?? globalDateRange.start;
          updates.end = prev.end ?? globalDateRange.end;
        }

        return { ...prev, ...updates };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedEntity?.entityId,
    selectedEntity?.excludedEntitiesVariables,
    selectedEntity?.timeoutId,
    selectedEntity?.start,
    selectedEntity?.end,
    globalDateRange.start,
    globalDateRange.end,
  ]);

  // Modal opener (shared by both EntityAnalysisModal and EntityInformationModal)
  const onAnalysisModalOpen = useCallback(
    (entity?: EntityInstance) => {
      const entityToOpen = entity || selectedEntity;
      if (entityToOpen) {
        setSelectedEntity(entityToOpen);
      }
      onAnalysisModalOpenInternal();
    },
    [selectedEntity, onAnalysisModalOpenInternal],
  );

  // Isolation modal opener
  const onIsolationOpen = useCallback(
    (type: EDRIntegration) => {
      setIsolationType(type);
      onIsolationOpenInternal();
    },
    [onIsolationOpenInternal],
  );

  const value = useMemo<EntitiesContextType>(
    () => ({
      analysisModal: {
        isOpen: isAnalysisModalOpen,
        onOpen: onAnalysisModalOpen,
        onClose: onAnalysisModalClose,
        activeTab,
        setActiveTab,
        modalNavigation,
      },
      suppressionModal: {
        isOpen: suppressionOpen,
        onOpen: onSuppressionOpen,
        onClose: onSuppressionClose,
      },
      isolationModal: {
        isOpen: isolationOpen,
        onOpen: onIsolationOpen,
        onClose: onIsolationClose,
        isolationType,
      },
      selectedEntity,
      setSelectedEntity,
      suppression,
    }),
    [
      isAnalysisModalOpen,
      onAnalysisModalOpen,
      onAnalysisModalClose,
      activeTab,
      modalNavigation,
      suppressionOpen,
      onSuppressionOpen,
      onSuppressionClose,
      isolationOpen,
      onIsolationOpen,
      onIsolationClose,
      isolationType,
      selectedEntity,
      suppression,
    ],
  );

  return (
    <EntitiesContext.Provider value={value}>
      <EntityEdrProvider>{children}</EntityEdrProvider>
    </EntitiesContext.Provider>
  );
};

export const useEntitiesContext = () => {
  const context = useContext(EntitiesContext);
  if (!context) {
    throw new Error(
      'useEntitiesContext must be used within an EntitiesProvider',
    );
  }
  return context;
};

export const useEntitiesContextSafe = () => {
  return useContext(EntitiesContext);
};

export { EntitiesContext };
